<template>
  <main role="main" class="premium-wrapper">
    <!-- Nền trắng tinh khôi -->

    <!-- Loading state -->
    <div v-if="isLoading" class="loading-full">
      <div class="loader-content">
        <b-spinner variant="primary" style="width: 3rem; height: 3rem;" class="mb-3" />
        <p class="text-white">{{ $t("loadingData") }}</p>
      </div>
    </div>

    <!-- ==================== GIAO DIỆN KHI DÙNG SECRET KEY (TỪ URL) ==================== -->
    <b-container 
      v-if="meeting && !isPathGm" 
      class="d-flex flex-column justify-content-start align-items-center min-vh-100 py-5"
    >
      <div v-if="isActiveMeeting" class="modern-card-container">
        <div class="modern-white-card p-4 p-md-5">
          <!-- Header -->
          <div class="form-header mb-4 text-center">
            <!-- <div class="icon-circle mb-3">
              <b-icon icon="camera-reels" class="text-primary"></b-icon>
            </div> -->
            <h2 class="meeting-title-modern">{{ meeting.name }}</h2>
            
            <!-- Trạng thái cuộc họp -->
            <div class="d-flex flex-wrap align-items-center justify-content-center mt-1" style="gap: 8px;">
              <div v-if="meeting.id" class="meeting-id-modern">
                ID: <span class="id-value">{{ meeting.id }}</span>
              </div>
              <div class="meeting-status-modern">
                <span class="status-dot" :class="meeting.status.toLowerCase()"></span>
                <span class="status-text">{{ statusText[meeting.status] }}</span>
              </div>
            </div>

            <div v-if="meeting.role === roleJoinMeeting.OPERATOR && meeting.status !== enumMeetingStatus.END" class="note-role mt-2">
               <b-icon icon="shield-check" class="mr-1"></b-icon>
               {{ $t("joinMeetingOperator")}} "{{ $t("roleAdmin") }}"
            </div>
          </div>

          <b-form 
            class="text-left modern-form" 
            id="join-meeting-form" 
            @submit.prevent="joinMeeting"
          >
            <!-- Form inputs -->
            <div v-if="meetingSecretKey" class="form-body">
              <!-- Họ và tên -->
              <b-form-group label-for="input-full-name" class="modern-group">
                <template v-slot:label>
                  <strong>{{ $t("fullName") }}</strong> <span class="text-danger">*</span>
                </template>
                <b-form-input 
                  id="input-full-name" 
                  class="modern-input-light" 
                  type="text" 
                  required 
                  minlength="2" 
                  maxlength="500" 
                  v-model="fullName" 
                  :placeholder="$t('namePlaceHolder')"                 
                  :disabled="isSubmitting" 
                  :pattern="fullNamePattern"
                  @invalid="$event.target.setCustomValidity($t('FormatInvalidName'))"
                  @input.native="$event.target.setCustomValidity('')"
                />
              </b-form-group>
              
              <!-- Tên tổ chức -->
              <b-form-group 
                v-if="shouldShowOrgField" 
                label-for="input-org-name"
                class="modern-group"
              >
                <template v-slot:label>
                  <strong>{{ $t('workOrganizationName') }}</strong>
                </template>
                <b-form-input 
                  id="input-org-name" 
                  type="text" 
                  class="modern-input-light"
                  maxlength="500" 
                  v-model="orgName"
                  :disabled="isSubmitting" 
                />
              </b-form-group>

              <!-- Mã truy cập -->
              <b-form-group label-for="input-access-code" class="modern-group mb-4">
                <template v-slot:label>
                  <strong>{{ $t("enterAccessCode") }}</strong> <span class="text-danger">*</span>
                </template>
                <b-form-input 
                  id="input-access-code" 
                  type="text" 
                  class="modern-input-light" 
                  required 
                  minlength="2"
                  :placeholder="$t('accessCodePlaceHolder')"
                  maxlength="32" 
                  v-model="accessCode" 
                  :disabled="isSubmitting || (meeting && meeting.isReOpen)" 
                />
              </b-form-group>
            </div>

            <!-- Thông báo lỗi -->
            <transition name="fade">
              <div v-if="formErrorMsg" class="modern-error-light mb-4">
                <b-icon icon="exclamation-circle-fill" class="mr-2"></b-icon>
                {{ formErrorMsg }}
              </div>
            </transition>

            <!-- Buttons -->
            <div class="button-section" v-if="shouldDisplayWebButton">
              <b-button 
                type="submit" 
                variant="primary" 
                class="btn-modern-primary w-100" 
                :disabled="isSubmitting"
              >
                {{ buttonLabel() }}
              </b-button>
            </div>

            <div v-if="shouldDisplayMobileButtons" class="button-section">
              <b-button 
                variant="primary" 
                class="btn-modern-primary w-100 mb-3" 
                @click="openApp($event)"
              >
                <b-icon icon="phone" class="mr-2"></b-icon>
                {{ $t("joinMeeting") }}
              </b-button>
              
              <b-button 
                v-if="!isOpenApp" 
                block 
                variant="outline-primary" 
                class="btn-modern-outline w-100" 
                :disabled="isSubmitting" 
                @click="joinMeeting()"
              >
                 <b-icon icon="globe" class="mr-2"></b-icon>
                {{ $t("joinOnBrowser") }}
              </b-button>
            </div>

            <!-- Đồng ý điều khoản -->
            <div class="terms-agreement-container mt-4">
                {{ $t("byJoiningMeetingYouAccept") }}
                <a href="https://vnptai.io/vi/dieukhoansudung" target="_blank" rel="noopener noreferrer" class="terms-link">{{ $t("signUpTermOfUse") }}</a>,
                <a href="https://vnptai.io/vi/baovedulieucanhan" target="_blank" rel="noopener noreferrer" class="terms-link">{{ $t("personalDataProtectionPolicy") }}</a>
                {{ $t("and") }}
                <a href="https://vnptai.io/vi/chinhsachbaomat" target="_blank" rel="noopener noreferrer" class="terms-link">{{ $t("signUpPrivacyPolicy") }}</a>
                {{ $t("productVnptGoMeet") }}
            </div>
          </b-form>
        </div>
      </div>

      <!-- Out of service -->
      <!-- Giao diện khi cuộc họp đã kết thúc -->
      <div v-else class="modern-card-container">
        <div class="modern-white-card p-4 p-md-5 text-center">
          <h2 class="meeting-title-modern mb-4">{{ meeting.name }}</h2>
          <div class="d-flex flex-wrap align-items-center justify-content-center mb-2" style="gap: 8px;">
            <div v-if="meeting.id" class="meeting-id-modern">
              ID: <span class="id-value">{{ meeting.id }}</span>
            </div>
            <div class="meeting-status-modern">
               <span class="status-dot" :class="meeting.status.toLowerCase()"></span>
               <span class="status-text">{{ $t("meetingStatusEnded") }}</span>
            </div>
          </div>
          <!-- <div class="button-section mt-3">
            <b-button 
              variant="primary" 
              class="btn-modern-primary w-100 mb-3" 
              @click="goToGm"
            >
              <b-icon icon="plus-circle" class="mr-2"></b-icon>
              {{ $t("joinWithAnotherCode") || "Tham gia cuộc họp khác" }}
            </b-button>
            <b-button 
              variant="outline-primary" 
              class="btn-modern-outline w-100" 
              href="/"
            >
              <b-icon icon="house-door" class="mr-2"></b-icon>
              {{ $t("portalHome") || "Về trang chủ" }}
            </b-button>
          </div> -->
        </div>
      </div>
    </b-container>

    <b-container 
      v-else-if="isPathGm" 
      class="d-flex flex-column justify-content-start align-items-center min-vh-100 py-5"
    >
    <div class="modern-card-container">
      <div class="modern-white-card p-4 p-md-5">
        <div class="form-header mb-4 text-center">
          <!-- <div class="icon-circle mb-3">
            <b-icon icon="door-open" class="text-secondary"></b-icon>
          </div> -->
          <h2 class="meeting-title-modern">{{ $t("inputInfoMeeting") || 'THAM GIA HỌP' }}</h2>
          <p class="meeting-subtitle-modern">{{ $t("joinMeetingDesc") || 'Điền thông tin mã tham gia để bắt đầu' }}</p>
        </div>
      
        <b-form 
          class="text-left modern-form" 
          id="join-meeting-form" 
          @submit.prevent="joinMeeting"
        >
          <div>
            <!-- Mã tham gia -->
            <b-form-group label-for="input-join-code" class="modern-group">
              <template v-slot:label>
                <strong>{{ $t("meetingCode") }}</strong> <span class="text-danger">*</span>
              </template>
              <b-form-input 
                id="input-access-code" 
                type="text" 
                class="modern-input-light" 
                required 
                minlength="2" 
                maxlength="500"
                :placeholder="$t('meetingCodePlaceHolder')"
                @paste="onPasteLinkHandler"
                ref="secretInput"
                v-model="meetingSecretKey" 
                :disabled="isSubmitting" 
              />
            </b-form-group>
            
            <!-- Mã truy cập -->
            <b-form-group label-for="input-access-code" class="modern-group">
              <template v-slot:label>
                <strong>{{ $t("enterAccessCode") }}</strong> <span class="text-danger">*</span>
              </template>
              <b-form-input 
                id="input-access-code" 
                type="text" 
                class="modern-input-light" 
                required 
                minlength="2"
                :placeholder="$t('accessCodePlaceHolder')"
                @paste="onPasteLinkHandler"
                maxlength="32" 
                v-model="accessCode" 
                :disabled="isSubmitting || (meeting && meeting.isReOpen)" 
              />
            </b-form-group>

            <!-- Họ và tên -->
            <b-form-group label-for="input-full-name" class="modern-group mb-4">
              <template v-slot:label>
                <strong>{{ $t("fullName") }}</strong> <span class="text-danger">*</span>
              </template>
              <b-form-input 
                id="input-full-name" 
                class="modern-input-light" 
                type="text" 
                required 
                minlength="2" 
                maxlength="500" 
                v-model="fullName" 
                :placeholder="$t('namePlaceHolder')"                 
                :disabled="isSubmitting || isNameFromAPI"
                :class="{ 'input-disabled-light': isSubmitting || isNameFromAPI }"
                :pattern="fullNamePattern"
                @invalid="$event.target.setCustomValidity($t('FormatInvalidName'))"
                @input.native="$event.target.setCustomValidity('')"
              />
            </b-form-group>
            
            <!-- Error messages -->
            <transition name="fade">
              <div v-if="formErrorMsg" class="modern-error-light mb-4">
                <b-icon icon="exclamation-circle-fill" class="mr-2"></b-icon>
                {{ formErrorMsg }}
              </div>
            </transition>

            <div class="button-section" v-if="shouldDisplayWebButton">
              <b-button 
                type="submit" 
                variant="primary" 
                class="btn-modern-primary w-100" 
                :disabled="isSubmitting"
              >
                {{ buttonLabel() }}
              </b-button>
            </div>

            <div v-if="shouldDisplayMobileButtons" class="button-section">
              <b-button 
                variant="primary" 
                class="btn-modern-primary w-100 mb-3" 
                @click="getMeetingInfoGmForm()"
              >
                <b-icon icon="phone" class="mr-2"></b-icon>
                {{ $t("joinMeeting") }}
              </b-button>
              
              <b-button 
                v-if="!isOpenApp" 
                block 
                variant="outline-primary" 
                class="btn-modern-outline w-100" 
                :disabled="isSubmitting" 
                @click="joinMeeting()"
              >
                <b-icon icon="globe" class="mr-2"></b-icon>
                {{ $t("joinOnBrowser") }}
              </b-button>
            </div>

            <!-- Đồng ý điều khoản -->
            <div class="terms-agreement-container mt-4">
                {{ $t("byJoiningMeetingYouAccept") }}
                <a href="https://vnptai.io/vi/dieukhoansudung" target="_blank" rel="noopener noreferrer" class="terms-link">{{ $t("signUpTermOfUse") }}</a>,
                <a href="https://vnptai.io/vi/baovedulieucanhan" target="_blank" rel="noopener noreferrer" class="terms-link">{{ $t("personalDataProtectionPolicy") }}</a>
                {{ $t("and") }}
                <a href="https://vnptai.io/vi/chinhsachbaomat" target="_blank" rel="noopener noreferrer" class="terms-link">{{ $t("signUpPrivacyPolicy") }}</a>
                {{ $t("productVnptGoMeet") }}
            </div>
          </div>
        </b-form>
      </div>
    </div>
      
    </b-container>

    <!-- Error meeting info -->
    <b-container v-if="!meeting && !isPathGm" class="min-vh-100 d-flex align-items-center justify-content-center">
      <div 
        v-if="formErrorMsg" 
        class="modern-white-card p-4 text-center" style="max-width: 400px;"
      >
        <b-icon icon="exclamation-triangle" variant="warning" font-scale="3" class="mb-3"></b-icon>
        <p class="text-dark">{{ formErrorMsg }}</p>
        <b-button variant="link" class="text-primary mt-2" @click="goToGm">
          {{ $t("joinWithAnotherCode") || "Tham gia bằng mã khác" }}
        </b-button>
      </div>
    </b-container>
  </main>
</template>

<style lang="scss" scoped>
.premium-wrapper {
  position: relative;
  min-height: 100vh;
  width: 100%;
  overflow-x: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f0f4f8 0%, #e2ebf0 100%);
  &::before {
    content: '';
    position: absolute;
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(59, 130, 246, 0.15) 0%, rgba(255, 255, 255, 0) 70%);
    top: -200px;
    left: -200px;
    border-radius: 50%;
  }
}

.loading-full {
  position: fixed;
  inset: 0;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(12px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loader-content {
  text-align: center;
  animation: float 2s ease-in-out infinite;
  
  p {
    font-weight: 600;
    color: #475569;
    letter-spacing: 0.5px;
  }
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

.modern-card-container {
  width: 100%;
  max-width: 480px;
  position: relative;
  z-index: 10;
  animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
}

.modern-white-card {
  background: #f3f9ff;
  backdrop-filter: blur(20px);
  border-radius: 28px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.6) inset;
  border: 1px solid rgba(255, 255, 255, 0.5);
  color: #1e293b;
  width: 100%;
  overflow: hidden;
  transition: transform 0.3s ease;
}

.form-header {
  margin-bottom: 2.5rem;
}

.meeting-title-modern {
  font-size: 26px;
  font-weight: 800;
  color: #0f172a;
  margin-bottom: 12px;
  letter-spacing: -0.5px;
  line-height: 1.3;
}

.meeting-subtitle-modern {
  color: #64748b;
  font-size: 14px;
  margin-top: -6px;
  margin-bottom: 16px;
}

.meeting-status-modern {
  display: inline-flex;
  align-items: center;
  background: #ffffff;
  padding: 6px 16px;
  border-radius: 9999px;
  border: 1px solid rgba(0,0,0,0.04);
  box-shadow: 0 2px 6px rgba(0,0,0,0.03);
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 8px;
  box-shadow: 0 0 8px currentColor;
  &.inprogress { background: #10b981; color: #10b981; }
  &.pending { background: #f59e0b; color: #f59e0b; }
  &.end { background: #64748b; color: #64748b; }
}

.status-text {
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.meeting-id-modern {
  display: inline-flex;
  align-items: center;
  background: #ffffff;
  padding: 6px 14px;
  border-radius: 9999px;
  border: 1px solid rgba(0, 0, 0, 0.04);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.03);
  font-size: 13px;
  color: #64748b;
  font-weight: 500;

  .b-icon {
    font-size: 14px;
    color: #64748b;
    margin-right: 4px;
  }

  .id-value {
    font-size: 13px;
    color: #475569;
    font-weight: 600;
    margin-left: 2px;
    letter-spacing: 0.5px;
  }
}

.note-role {
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  background: #f1f5f9;
  padding: 6px 14px;
  border-radius: 12px;
  margin-top: 16px;
}

.modern-group {
  margin-bottom: 24px;
  
  label {
    font-size: 14px;
    font-weight: 600;
    color: #334155;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    strong { font-weight: 600; }
  }
}

.modern-input-light {
  height: 52px !important;
  background: rgba(255, 255, 255, 0.9) !important;
  border: 1px solid #cce3ff !important;
  border-radius: 14px !important;
  color: #0f172a !important;
  font-size: 18px !important;
  font-weight: 500 !important;
  // box-shadow: 0 2px 6px rgba(0, 0, 0, 0.02), 0 0 0 1px #e2e8f0 inset !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;

  &:focus {
    background: #fff !important;
    border-color: transparent !important;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15), 0 0 0 2px #3b82f6 inset !important;
    outline: none !important;
  }

  &::placeholder {
    color: #94a3b8 !important;
    font-weight: 400 !important;
  }
}

.input-disabled-light {
  background: #f8fafc !important;
  box-shadow: 0 0 0 1px #e2e8f0 inset !important;
  color: #94a3b8 !important;
  cursor: not-allowed;
}

.modern-error-light {
  background: #fef2f2;
  color: #dc2626;
  padding: 14px 16px;
  border-radius: 14px;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  border: 1px solid #fee2e2;
  animation: slideInError 0.3s ease;
}

@keyframes slideInError {
  from { opacity: 0; transform: translateY(-5px); }
  to { opacity: 1; transform: translateY(0); }
}

.btn-modern-primary {
  height: 54px;
  border-radius: 14px;
  font-weight: 600;
  font-size: 16px;
  background: linear-gradient(135deg, #137ae1 0%, #004f9e 100%);
  border: none;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 20px rgba(37, 99, 235, 0.25), inset 0 1px 0 rgba(255,255,255,0.15);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    // transform: translateY(-2px);
    box-shadow: 0 12px 25px rgba(37, 99, 235, 0.35), inset 0 1px 0 rgba(255,255,255,0.15);
  }

  &:active {
    transform: translateY(1px);
    box-shadow: 0 4px 10px rgba(37, 99, 235, 0.2);
  }

  &:disabled {
    background: #cbd5e0;
    box-shadow: none;
    cursor: not-allowed;
    transform: none;
  }
}

.btn-modern-outline {
  height: 54px;
  border-radius: 14px;
  font-weight: 600;
  font-size: 16px;
  border: 2px solid #e2e8f0;
  color: #475569;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f8fafc;
    border-color: #cbd5e0;
    color: #1e293b;
  }
}

.fade-enter-active, .fade-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}
.fade-enter, .fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

@media (max-width: 576px) {
  .premium-wrapper::before {
    display: none;
  }
  .modern-card-container {
    max-width: 100%;
    margin: 0;
  }
  .modern-white-card {
    border-radius: 0;
    min-height: 100vh;
    padding: 40px 24px !important;
    box-shadow: none;
    border: none;
    background: #f3f9ff;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
}

.button-section {
  margin-top: 32px;
}

.terms-agreement-container {
  font-size: 13px;
  line-height: 1.6;
  color: #64748b;
  text-align: center;
  // margin-bottom: 20px;
  font-weight: 400;
}

.terms-link {
  color: #137ae1;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease, text-decoration 0.2s ease;
  text-decoration: underline;
  &:hover {
    color: #004f9e;
    text-decoration: underline;
  }
}
</style>

<script lang="ts">
import { Component, Vue, Watch } from 'vue-property-decorator';
import _ from "lodash";
import BaseComponent from "@/components/BaseComponent";

import * as meetingService from "@/service/meetingService";
import * as util from "@/service/util";
import UAParser from "ua-parser-js";
import { MeetingStatus } from '../enums/MeetingStatus';
import {enumErrorType} from "@/views/enums/ErrorType";
import { JoinWithNameParam } from "@/model/JoinWithNameParam";
import { RoleJoinMeeting } from "@/views/enums/UserRole";

@Component
export default class JoinMeetingForm extends BaseComponent {

  // ==================== DATA PROPERTIES ====================
  
  // Enum và constants cho hiển thị
  readonly statusText = {
    PENDING: this.$t("meetingStatusPending").toString(),
    INPROGRESS: this.$t("meetingStatusInProgress").toString(),
    END: this.$t("meetingStatusEnded").toString(),
    CANCEL: this.$t("meetingStatusCancelled").toString(),
  };

  readonly statusBadgeVariant = {
    PENDING: "warning",
    INPROGRESS: "success",
    END: "secondary",
    CANCEL: "danger"
  };

  // Enum types
  enumMeetingStatus = MeetingStatus;
  enumErrorType = enumErrorType;
  roleJoinMeeting = RoleJoinMeeting;

  // Links download ứng dụng cho từng nền tảng
  readonly mobile = "https://gomeet01.vnpt.vn/storage";
  readonly android = "https://play.google.com/store/apps/details?id=com.vnpt.gomeetv3";
  readonly ios = "https://apps.apple.com/vn/app/vnpt-gomeet-v3/id1662423818";
  readonly macos = "https://gomeet01.vnpt.vn/storage/desktop/mac/gomeet.dmg";
  readonly windows = "https://gomeet01.vnpt.vn/storage/desktop/win/gomeet.exe";
  readonly linux = null;
  
  // Thông tin cuộc họp
  meeting = null;
  meetingSecretKey: string = null;

  // Lưu giá trị ban đầu của mã tham gia để so sánh khi người dùng nhập giá trị mới
  originalMeetingSecretKey = "";
  
  // Thông tin người dùng
  fullName = "";

  // Đánh dấu tên lấy từ API (disable khi là host hoặc link private)
  isNameFromAPI = false;
  orgName = "";
  accessCode = "";
  paramEmail: string = '';
  ver = "";
  
  // Trạng thái UI
  isLoading = true;
  isSubmitting = false;
  isOpenApp = false; // Đánh dấu đã click mở app
  formErrorMsg = "";
  
  // Link và phiên làm việc
  sessionToken = "";
  joinUrl = "";
  appDeepLink = "";
  appDownloadLink = "";
  coreVersion = "";
  
  // Widget và cấu hình
  haveWidget = false;
  isPathGm = false; // Đánh dấu path /gm (nhập thủ công)
  shouldShowOrgField = false;
  isMobile = false;

  // Key để lưu/restore data từ sessionStorage
  readonly STORAGE_KEY = 'join_meeting_form_data';

  // ==================== COMPUTED PROPERTIES ====================
  
  /**
   * Kiểm tra cuộc họp có đang hoạt động không
   * (Không phải trạng thái END hoặc CANCEL)
   * Đặc biệt: Với trạng thái cuộc họp là END (đã kết thúc) nhưng người dùng là
   * OPERATOR (Quản trị) và cuộc họp được cấu hình cho phép mở lại (isReOpen = true),
   * vẫn coi là hoạt động để hiển thị form tham gia / mở lại.
   */
  get isActiveMeeting(): boolean {
    if (this.meeting?.status === MeetingStatus.END && this.meeting?.role === RoleJoinMeeting.OPERATOR && this.meeting?.isReOpen) {
      return true;
    }
    return ![MeetingStatus.END, MeetingStatus.CANCEL].includes(
      this.meeting?.status as MeetingStatus
    );
  }

  /**
   * Kiểm tra có nên hiển thị nút join trên web không
   * Hiển thị khi: không có appDeepLink HOẶC (không phải mobile VÀ có appDeepLink)
   * Đặc biệt: Đối với luồng mở lại cuộc họp của OPERATOR, luôn bắt buộc hiển thị
   * nút Web (Mở lại cuộc họp) và không đi qua luồng mở ứng dụng di động/desktop.
   */
  get shouldDisplayWebButton(): boolean {
    if (this.meeting?.status === MeetingStatus.END && this.meeting?.role === RoleJoinMeeting.OPERATOR && this.meeting?.isReOpen) {
      return true;
    }
    return !this.appDeepLink || (!this.isMobile && !!this.appDeepLink);
  }

  /**
   * Kiểm tra có nên hiển thị nút join mobile không
   * Hiển thị khi: là mobile VÀ có appDeepLink
   * Đặc biệt: Ẩn nút mobile khi OPERATOR đang mở lại cuộc họp đã kết thúc.
   */
  get shouldDisplayMobileButtons(): boolean {
    if (this.meeting?.status === MeetingStatus.END && this.meeting?.role === RoleJoinMeeting.OPERATOR && this.meeting?.isReOpen) {
      return false;
    }
    return this.isMobile && !!this.appDeepLink;
  }

  /**
   * Xác định nhãn hiển thị cho nút tham gia/bắt đầu cuộc họp.
   * - Nếu là OPERATOR và cuộc họp chưa bắt đầu (PENDING): hiển thị "Bắt đầu cuộc họp".
   * - Nếu là OPERATOR và cuộc họp đã kết thúc (END) nhưng được phép mở lại: hiển thị "Mở lại cuộc họp".
   * - Ngược lại: hiển thị "Tham gia".
   */
  buttonLabel(): string {
    if (this.meeting?.role === RoleJoinMeeting.OPERATOR) {
      if (this.meeting?.status === MeetingStatus.PENDING) {
        return this.$t("meetingStart").toString();
      }
      if (this.meeting?.status === MeetingStatus.END && this.meeting?.isReOpen) {
        return this.$t("meetingReopen").toString();
      }
    }
    return this.$t("joinMeetingBrowser").toString();
  }

  // ==================== LIFECYCLE HOOKS ====================

  // Watch: Khi mã tham gia thay đổi => cho phép nhập lại tên
  @Watch('meetingSecretKey')
  onMeetingSecretKeyChange(newValue: string) {
    if (this.isNameFromAPI === false) return;
    if (newValue.trim() === this.originalMeetingSecretKey.trim()) {
      this.isNameFromAPI = true;
    } 
    // Nếu khác mã gốc => mở cho nhập tên
    else {
      this.isNameFromAPI = false;
    }
  }

  // Watch để tự động lưu data khi thay đổi
  @Watch('accessCode')
  @Watch('fullName')
  @Watch('orgName')
  onFormDataChange() {
    this.saveFormDataToStorage();
  }

  /**
   * Hook mounted - Chạy khi component được gắn vào DOM
   * Khởi tạo dữ liệu ban đầu: lấy params từ route, xác định device, load meeting
   */
  mounted() {
    this.initFromRoute();
    this.restoreFormDataFromStorage(); // Restore data từ storage
    this.detectDevice();
    
    // Xác định flow: /gm (nhập thủ công) hoặc có secret key
    const gm = this.$route.path.startsWith("/gm");
    if(gm) {
      // Flow nhập thủ công
      this.isPathGm = true;
      this.loadMeetingWithoutSecretKey();
    } else {
      // Flow có secret key từ URL
      this.loadMeetingBySecretKey();
    }

    // Auto focus vào field rỗng đầu tiên
    this.$nextTick(() => {
      this.focusFirstEmptyInput();
    });

    window.addEventListener('pageshow', this.handlePageShow);
  }

  // Khi component được activate lại (từ cache hoặc back)
  activated() {
    this.restoreFormDataFromStorage(); // Restore khi back
    this.$forceUpdate();
    this.$nextTick(() => this.focusFirstEmptyInput());
  }

  /**
   * Hook unmounted - Cleanup khi component bị xóa khỏi DOM
   * Dispatch event để reset version
   */
  unmounted() {
    var evt = new CustomEvent("versionChange", { detail: { type: null } });
    window.dispatchEvent(evt);
  }

  /**
   * Lifecycle hook: beforeDestroy
   * Cleanup event listeners trước khi component bị destroy
   */
  beforeDestroy() {
    // Remove pageshow event listener để tránh memory leak
    window.removeEventListener('pageshow', this.handlePageShow);
  }

  /**
   * Xử lý khi trang được hiển thị (bao gồm cả khi restore từ bfcache)
   * 
   * Browser's bfcache (back/forward cache) lưu snapshot của trang để tăng tốc độ back/forward.
   * Khi trang được restore từ bfcache:
   * - mounted() KHÔNG được gọi lại
   * - Data cũ vẫn còn trong DOM
   * - Cần force restore từ sessionStorage để có data mới nhất
   * 
   * @param event PageTransitionEvent chứa thông tin về cách trang được load
   */
  handlePageShow(event: PageTransitionEvent) {
    if (event.persisted) {
      //load lại giao diện
      window.location.reload();
      // Force restore data từ sessionStorage
      this.restoreFormDataFromStorage();

      // Force để update UI
      this.$forceUpdate();

      //
      this.formErrorMsg = null;
      this.isSubmitting = false;
    }
  }

  // ==================== INITIALIZATION METHODS ====================

  /**
   * Khởi tạo dữ liệu từ route params và query
   * 
   * Được gọi khi:
   * - Component mounted
   * - Route thay đổi
   * 
   * Logic:
   * - Lấy params/query từ URL
   * - CHỈ set nếu có giá trị (không overwrite data đã paste từ storage)
   * - Ưu tiên: sessionStorage > route params > cookie > empty
   * 
   * Ví dụ URL: /join-meeting/abc123?accessCode=xyz789&fullName=John
   */
  initFromRoute() {
    const { params, query, meta } = this.$route;
    
    // Decode URI components để xử lý ký tự đặc biệt
    const newSecretKey = decodeURIComponent(params.meetingSecretKey || '');
    const newAccessCode = ((query.accessCode || query.adminCode)?.toString().trim() || "").split(' ')[0].trim();
    const newFullName = query.fullName?.toString() || this.getCookie('p_join_name') || '';
    
    // Chỉ set nếu có giá trị từ route (không overwrite data đã paste)
    // Logic: Nếu user đã paste link (data có trong storage), không ghi đè
    // Chỉ set từ route khi chưa có data hoặc load lần đầu
    if (newSecretKey) {
      this.$set(this, 'meetingSecretKey', newSecretKey);
    }
    if (newAccessCode) {
      this.$set(this, 'accessCode', newAccessCode);
    }
    if (newFullName) {
      this.$set(this, 'fullName', newFullName);
    }
    
    // Set các params khác (không cần check vì không ảnh hưởng đến paste)
    this.paramEmail = decodeURIComponent(query.email as string || '');
    
    // Kiểm tra có cần hiển thị field tên tổ chức không
    this.shouldShowOrgField = meta?.mayHaveOrgField && window["vnptEConferencePortalJoinMeetingWithOrgName"];
    
    // Lấy version từ query (nếu có)
    this.ver = query.v?.toString().trim() || "";
  }

  /**
   * Phát hiện thiết bị
   */
  detectDevice() {
    // Xác định thiết bị có phải mobile không
    const uap = new UAParser();
    const osname = uap.getOS().name.toLowerCase();
    
    // iPad với Mac OS được coi là mobile
    if(osname == "mac os" && uap.getDevice().type == "tablet") {
      this.isMobile = true;
    } else {
      // Android và iOS là mobile
      this.isMobile = ["android", "ios"].includes(osname);
    }
  }

  /**
   * Load meeting khi không có secret key (flow nhập thủ công /gm)
   * Chỉ cấu hình deep link, không gọi API
   */
  async loadMeetingWithoutSecretKey(){
    try{
      this.isLoading = true;
      this.meetingSecretKey = null;

      // Dispatch event thông báo không có secret key
      const evt = new CustomEvent("versionChange", {
        detail: { type: "noSecretKey" },
      });
      window.dispatchEvent(evt);

      // Cấu hình deep link
      this.handleDeeplink();
    }
    catch(e){
      this.formErrorMsg = this.showError(e,enumErrorType.MESSAGE);
    }finally {
      this.isLoading = false;
    }
  }

  /**
   * Load thông tin meeting từ secret key
   * Gọi API getBySecretKey để lấy thông tin cuộc họp
   */
  async loadMeetingBySecretKey() {
    try {
      this.isLoading = true;
      
      // Gọi API lấy thông tin cuộc họp
      this.meeting = await meetingService.getBySecretKey(this.meetingSecretKey, this.accessCode);
      this.coreVersion = this.meeting.version;
      
      // Dispatch event với version của cuộc họp
      var evt = new CustomEvent("versionChange", { detail: { type: this.coreVersion } });
      window.dispatchEvent(evt);
      
      // Cấu hình deep link
      this.handleDeeplink();
    } catch (e) {      
      this.formErrorMsg = this.showError(e,enumErrorType.MESSAGE);
    } finally {
      this.isLoading = false;
    }
  }

  // ==================== DEEP LINK METHODS ====================

  /**
   * Xử lý deep link version 4 (cho cả Desktop và Mobile)
   * - Xác định OS và device type
   * - Thiết lập link download phù hợp
   * - Build deep link URL với params
   */
  handleDeeplink() {    
    const uap = new UAParser();
    const osname = uap.getOS().name.toLowerCase();
    let isMobile = false;

    // Xác định link download dựa trên OS và device
    if (["mac os", "macos", "mac"].includes(osname) && uap.getDevice().type != "tablet") {
      // MacOS desktop
      this.appDownloadLink = this.macos;
    } else if (osname.includes("windows")) {
      // Windows
      this.appDownloadLink = this.windows;
    } else if (osname === "android") {
      // Android mobile
      this.appDownloadLink = this.android;
      isMobile = true;
    } else if (osname === "ios" || ["mac os", "macos", "mac"].includes(osname) && uap.getDevice().type == "tablet") {
      // iOS hoặc iPadOS
      this.appDownloadLink = this.ios;
      isMobile = true;
    } else if (navigator.userAgent.toLowerCase().includes("linux")) {
      // Linux
      this.appDownloadLink = this.linux;
    }    

    // Cấu hình deep link nếu có app download link
    if (this.appDownloadLink) {
      // Thêm meta viewport để responsive trên mobile
      if (!document.head.querySelector(`meta[name="viewport"]`)) {
        const metaTag = document.createElement('meta');
        metaTag.name = "viewport";
        metaTag.content = "width=device-width,initial-scale=1.0";
        document.head.appendChild(metaTag);
      }

      // Build URL params cho deep link
      // Kết hợp fullName và orgName thành name
      var usp = new URLSearchParams({
        roomId: this.meetingSecretKey,
        accessCode: this.accessCode,
        name: util.joinStrings([this.fullName, this.orgName], " ")
      });

      // Thêm version param nếu có
      if(this.ver != ""){
        usp = new URLSearchParams({
          roomId: this.meetingSecretKey,
          accessCode: this.accessCode,
          v : this.ver,
          name: util.joinStrings([this.fullName, this.orgName], " ")
        });
      }

      // Build deep link URL
      // v4 cho desktop, v3 cho mobile
      this.appDeepLink = `gomeetv4://${location.host}?${usp.toString()}`;
      if(isMobile) {
        this.appDeepLink = `gomeetv3://${location.host}?${usp.toString()}`;
      }
    }
  }

  // ==================== FORM SUBMISSION METHODS ====================

  /**
   * Submit form tham gia cuộc họp
   * - Chuẩn bị params (fullName, org, accessCode, email)
   * - Gọi API join (joinBySecretKey hoặc joinWithName tùy độ dài secret key)
   * - Lưu tên vào cookie
   * - Redirect hoặc cấu hình deep link
   */
  async _submitForm() {
    try {
      this.isSubmitting = true;
      this.formErrorMsg = null;
      
      // Chuẩn bị params để gọi API
      let param: JoinWithNameParam = {
        meetingSecretKey: this.meetingSecretKey,
        fullName: this.fullName.trim() || undefined,
        org: this.orgName.trim() || undefined,
        accessCode: this.accessCode,
      };

      // Thêm email nếu hợp lệ
      if (util.isValidEmail(this.paramEmail)) {
        param.email = this.paramEmail;
      }

      // Gọi API phù hợp dựa trên độ dài secret key
      // - Secret key > 32 ký tự: dùng joinBySecretKey (link đầy đủ)
      // - Secret key <= 32 ký tự: dùng joinWithName (mã ngắn)
      let res;
      if (this.meetingSecretKey.length > 32) {
        res = await meetingService.joinBySecretKey(param);
      } else {
        res = await meetingService.joinWithName(param);
      }

      // Lưu tên người dùng vào cookie để dùng lại sau (365 ngày)
      this.saveCookie('p_join_name', this.fullName.trim(), 365);
      
      // Lưu session token và join URL từ response
      this.sessionToken = res.sessionToken;
      
      if (this.sessionToken) {
        this.joinUrl = res.joinUrl;
        
        // Set core version mặc định nếu chưa có
        if(!this.coreVersion) {
          this.coreVersion = 'v3';
        }
        
        // Cập nhật deep link với session token
        // window[" "] là một trick để lưu session token vào global scope
        this.appDeepLink = window[" "] + this.sessionToken;
        this.handleDeeplink();
        
        // Redirect đến join URL nếu không có app download link
        if (!this.appDownloadLink) {
          location.href = res.joinUrl;
        }
      } else {
        // Nếu không có session token, redirect trực tiếp
        location.href = res.joinUrl;
      }
    } catch (e) {
      this.formErrorMsg = this.showError(e,enumErrorType.MESSAGE);
    } finally {
      this.isSubmitting = false;
    }
  }

  /**
   * Validate và tham gia cuộc họp
   * - Kiểm tra các trường bắt buộc (secretKey, accessCode)
   * - Gọi submitForm() để xử lý logic join
   * - Redirect đến joinUrl nếu thành công
   */
  async joinMeeting() {
    this.formErrorMsg = null;
    
    // Validate mã tham gia (secretKey)
    if (!this.meetingSecretKey || this.meetingSecretKey.trim().length < 2) {
      this.formErrorMsg = this.$t("joinCodeRequired") as string;
      return;
    }

    // Validate mã truy cập
    if (!this.accessCode || this.accessCode.trim().length < 2) {
      this.formErrorMsg = this.$t("AccessCodeRequired") as string;
      return;
    }
    
    try {
      this.isSubmitting = true;
      const res = await this._submitForm();
      
      // Redirect đến join URL nếu có
      if (this.joinUrl) {
        window.location.href = this.joinUrl;
      }

    } catch (e) {
      this.formErrorMsg = this.showError(e,enumErrorType.MESSAGE).toString();
    } finally {
      this.isSubmitting = false;
    }
  }

  /**
   * Lấy thông tin meeting từ form nhập thủ công (path /gm)
   * Sử dụng cho trường hợp mobile app
   * - Load thông tin meeting
   * - Nếu meeting đang diễn ra (INPROGRESS), mở app
   */
  async getMeetingInfoGmForm() {
    try{
      this.isSubmitting = true;
      this.formErrorMsg = null;
      
      // Load thông tin cuộc họp từ secret key
      this.meeting = await meetingService.getBySecretKey(this.meetingSecretKey, this.accessCode);
      
      // Nếu cuộc họp đang diễn ra, build deep link và mở app
      if(this.meeting.status == MeetingStatus.INPROGRESS){
        const usp = new URLSearchParams({
          roomId: this.meetingSecretKey || "",
          accessCode: this.accessCode || "",
        });

        // Build deep link cho mobile (v3)
        this.appDeepLink = `gomeetv3://${location.host}?${usp.toString()}`;
        this.openApp(null);
      }
    }
    catch (e) {
      this.formErrorMsg = this.showError(e,enumErrorType.MESSAGE).toString();
    } finally {
      this.isSubmitting = false;
    }
  }

  // ==================== APP INTERACTION METHODS ====================

  /**
   * Mở ứng dụng desktop/mobile bằng deep link
   * - Redirect đến deep link để mở app
   * - Nếu app không mở sau 3 giây, redirect đến store để download
   */
  openApp(event) {
    // Chặn mở app nhiều lần
    if (this.isOpenApp) {
      return;
    }

    // Đánh dấu đã click mở app
    this.isOpenApp = true;

    // Prevent default event nếu có
    if (event) {
      event.preventDefault();
    }

    // Thử mở app bằng deep link
    window.location.href = this.appDeepLink;

    // Fallback: Nếu sau 3 giây app chưa mở, redirect đến store
    const now = Date.now();
    setTimeout(() => {
      // Nếu thời gian elapsed < 3.5s nghĩa là chưa chuyển sang app
      // => App chưa được mở, redirect đến store để download
      if (Date.now() - now < 3500) {
        this.isOpenApp = false;
        window.location.href = this.appDownloadLink;
      }
    }, 3000);
  }
  
  /**
   * Điều hướng về trang nhập mã tham gia (/gm)
   */
  goToGm() {
    this.$router.push('/gm').catch(() => {}).finally(() => {
       window.location.reload();
    });
  }

  /**
   * Tải ứng dụng desktop/mobile
   * Mở link download trong tab mới
   */
  downloadApp(event) {
    event.preventDefault();
    event.stopPropagation();
    window.open(this.appDownloadLink, "_blank");
  }

  // ==================== INPUT HANDLING METHODS ====================

  /**
   * Handler wrapper cho paste event
   * Đảm bảo method luôn tồn tại ngay cả khi component được re-render
   */
  onPasteLinkHandler(e) {
    if (typeof this.handlePasteLink === 'function') {
      this.handlePasteLink(e);
    }
  }
  /**
   * Xử lý khi người dùng paste link vào ô input
   * - Tự động parse link để lấy meetingSecretKey và accessCode
   * - Hỗ trợ các format link: /join-meeting/, /host/, /join/
   */
  async handlePasteLink(e) {

    this.formErrorMsg = null; 
    this.isNameFromAPI = false;

    // Lấy nội dung từ clipboard
    const text = e.clipboardData?.getData('text');

    // Nếu không có text thì return
    if (!text) return;

    // Regex pattern để parse link GoMeet
    // Format: .../join-meeting/{secretKey}?accessCode={code} (hoặc adminCode={code})
    // hoặc: .../host/{secretKey}?accessCode={code} (hoặc adminCode={code})
    // hoặc: .../join/{secretKey}?accessCode={code} (hoặc adminCode={code})
    const regex = /\/(join-meeting|host|join)\/([^?]+)\?(?:accessCode|adminCode)=([^&]+)/i;
    const match = text.match(regex);
    if (!match) return; // không đúng format link thì bỏ qua

    // Ngăn paste mặc định (để không dán link vào input)
    e.preventDefault();

    const [, pathType, secretKey, accessCode] = match;

    // Giải mã dữ liệu
    this.meetingSecretKey = decodeURIComponent(secretKey);
    this.accessCode = decodeURIComponent(accessCode);
    //Nếu là host hoặc join-meeting thì gọi hàm lấy thông tin
    if (["join-meeting", "host"].includes(pathType)) {
      await this.fetchMeetingInfo(this.meetingSecretKey);
    }

    // Lưu dữ liệu form
    this.saveFormDataToStorage();

  }

  /**
   * Tự động focus vào ô input rỗng đầu tiên
   * Hiện tại chỉ focus vào ô secret input nếu trống
   */
  focusFirstEmptyInput() {
    const secretEl = this.$refs.secretInput as HTMLInputElement;

    // Focus vào ô mã tham gia nếu trống
    if (!this.meetingSecretKey && secretEl) {
      secretEl.focus();
    }
  }

  // ==================== COOKIE METHODS ====================

  /**
   * Lưu cookie với thời gian hết hạn
   */
  saveCookie(name: string, value: string, days: number) {
    let expires = "";
    
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + (days*24*60*60*1000));
      expires = "; expires=" + date.toUTCString();
    }
    
    document.cookie = name + "=" + (value || "")  + expires + "; path=/";
  }

  /**
   * Đọc giá trị cookie theo tên
   */
  getCookie(name: string) {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    
    for(let i=0;i < ca.length;i++) {
      let c = ca[i];
      // Trim khoảng trắng đầu
      while (c.charAt(0)==' ') c = c.substring(1,c.length);
      // Tìm cookie theo tên
      if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length,c.length);
    }
    
    return null;
  }

  // ==================== SESSION STORAGE ====================

   /**
   * Lưu dữ liệu form vào sessionStorage
   * 
   * Mục đích: Khi user paste link và redirect sang trang khác, sau đó back lại,
   * data đã paste sẽ không mất mà được restore lại từ sessionStorage
   * 
   * Lưu ý: sessionStorage tự động xóa khi đóng tab/browser
   */
  saveFormDataToStorage() {
    if(!this.isPathGm) return; 
    try {
      const data = {
        meetingSecretKey: this.meetingSecretKey,  // Mã tham gia cuộc họp
        accessCode: this.accessCode,              // Mã truy cập
        fullName: this.fullName,                  // Họ tên người dùng
        isNameFromAPI: this.isNameFromAPI,       // Trạng thái disable input fullName   
        orgName: this.orgName,                    // Tên tổ chức
        timestamp: Date.now()                     // Thời điểm lưu (dùng để check expiry)
      };
      
      // Lưu vào sessionStorage dưới dạng JSON string
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      // Xử lý lỗi nếu không thể lưu (ví dụ: storage bị disable)
    }
  }

  /**
   * Khôi phục dữ liệu form từ sessionStorage
   * 
   * Được gọi khi:
   * - Component mounted (lần đầu load trang)
   * - Component activated (khi back lại từ trang khác)
   * 
   * Logic:
   * - Chỉ restore data nếu mới hơn 10 phút (tránh dùng data cũ)
   * - Dùng Vue.$set để đảm bảo Vue reactivity
   * - Force update để re-render UI
   */
  restoreFormDataFromStorage() {
    try {

      // Lấy data đã lưu từ sessionStorage
      const stored = sessionStorage.getItem(this.STORAGE_KEY);

      if (!stored) {
        return;
      }

      // Parse JSON string thành object
      const data = JSON.parse(stored);

      // Kiểm tra data có quá cũ không (10 phút)
      const tenMinutes = 10 * 60 * 1000;
      const isDataFresh = Date.now() - data.timestamp < tenMinutes;

      if (!isDataFresh) {
        // Data quá cũ (> 10 phút), xóa đi để tránh dùng nhầm
        sessionStorage.removeItem(this.STORAGE_KEY);
        return;
      }

      // Đảm bảo DOM đã ready
      this.$nextTick(() => {

        if (data.meetingSecretKey) {
          this.$set(this, 'meetingSecretKey', data.meetingSecretKey);
          this.originalMeetingSecretKey = data.meetingSecretKey; 
        }

        if (data.accessCode) {
          this.$set(this, 'accessCode', data.accessCode);
        }

        if (data.fullName) {
          this.$set(this, 'fullName', data.fullName);
        }

        if (data.orgName) {
          this.$set(this, 'orgName', data.orgName);
        }
        this.isNameFromAPI = data.isNameFromAPI || false;

        this.$forceUpdate();
      });

    } catch (e) {
      console.error('Error restoring from session storage:', e);
    }
  }

  /**
   * Xóa dữ liệu form khỏi sessionStorage
   * 
   * Được gọi sau khi:
   * - Submit form thành công
   * - User đã join meeting
   * 
   * Mục đích: Cleanup data để tránh restore nhầm cho lần join khác
   */
  clearFormDataStorage() {
    try {
      sessionStorage.removeItem(this.STORAGE_KEY);
    } catch (e) {
    }
  }
  /**
   * Lấy thông tin vai trò người tham gia cuộc họp từ secret key
   * Dùng cho flow nhập thủ công (/gm)
   */
  async fetchMeetingInfo(secretKey) {
    try {
      // Gọi API lấy thông tin cuộc họp
      const meeting = await meetingService.getBySecretKey(secretKey, this.accessCode);

      // Lưu vào biến trong component
      this.meeting = meeting;
      this.fullName = meeting?.joinName || "";

      // Disable input tên
      this.isNameFromAPI = true;

    } catch (e) {
      this.formErrorMsg = this.showError(e,enumErrorType.MESSAGE).toString();
    }
  }
}
</script>
